import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Bar } from "recharts";
import { Button } from "@/components/ui/button";
import { amiGet, amiPost, Card, Kpi, Loading, NotYet, StatusRow, num, pct, tsFmt, isoFmt, errMsg } from "@/lib/ami";

const KIND = { beobachtung: "Beobachtung", schaetzung: "Schätzung", hypothese: "Hypothese", historische_statistik: "Historische Statistik" };
export const KindBadge = ({ kind }) => (
  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${kind === "hypothese" ? "border-amber-400/40 text-amber-300" : "border-border text-muted-foreground"}`} data-testid={`kind-${kind}`}>{KIND[kind] || kind}</span>
);

function Flow({ f }) {
  return (
    <Card title="Order Flow" testid="ami-intel-flow" actions={<KindBadge kind="beobachtung" />}>
      <div className="grid grid-cols-3 gap-4 text-sm">
        {["5m", "1h", "24h"].map((k) => {
          const v = f[k].value;
          return (
            <div key={k} data-testid={`ami-flow-${k}`}>
              <div className="text-xs text-muted-foreground">{k}</div>
              <div className={`font-mono text-lg ${v.delta_btc >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{num(v.delta_btc, 1)} BTC</div>
              <div className="text-xs text-muted-foreground">Kaufanteil {v.buy_share_pct == null ? "–" : num(v.buy_share_pct, 1) + " %"}</div>
              <div className="text-xs text-muted-foreground">Große Orders: +{num(v.large_buy_btc, 0)} / −{num(v.large_sell_btc, 0)}</div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Delta = aggressive Käufe minus Verkäufe. Gezählt wird ab Start der Aufzeichnung.</p>
    </Card>
  );
}

function CvdChart({ inst }) {
  const q = useQuery({ queryKey: ["ami-flow", inst], queryFn: () => amiGet("/intel/orderflow", { inst, hours: 24 }), refetchInterval: 60000 });
  if (q.isLoading) return <Loading />;
  const rows = q.data?.rows || [];
  if (rows.length < 2) return <NotYet text="Noch zu wenige Minutenwerte – der Datenstrom läuft, Werte entstehen jede Minute." />;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={rows}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="ts" tickFormatter={(t) => new Date(t).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" minTickGap={50} />
        <YAxis yAxisId="p" domain={["auto", "auto"]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={64} tickFormatter={(v) => num(v, 0)} />
        <YAxis yAxisId="c" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={50} tickFormatter={(v) => num(v, 0)} />
        <Tooltip labelFormatter={tsFmt} formatter={(v, n) => [num(v, 1), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
        <Bar yAxisId="c" dataKey="delta" name="Delta (BTC)" fill="hsl(var(--primary))" opacity={0.5} />
        <Line yAxisId="c" dataKey="cvd" name="CVD (BTC)" stroke="#f59e0b" dot={false} strokeWidth={1.5} />
        <Line yAxisId="p" dataKey="c" name="Kurs" stroke="hsl(var(--foreground))" dot={false} strokeWidth={1.25} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function Zones({ z }) {
  if (!z.value) return <NotYet text="Liquiditätsbänder entstehen jede Minute." />;
  const row = (x, side) => (
    <div key={side + x.px_from} className="flex justify-between text-xs font-mono py-1 border-b border-border/50">
      <span>{num(Math.min(x.px_from, x.px_to), 0)}–{num(Math.max(x.px_from, x.px_to), 0)}</span><span>{num(x.btc, 1)} BTC · {num(x.x_median, 1)}×</span>
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="ami-intel-zones">
      <div><div className="text-xs text-rose-300 mb-1">Ask-Zonen (über Kurs)</div>{z.value.asks.map((x) => row(x, "a"))}</div>
      <div><div className="text-xs text-emerald-300 mb-1">Bid-Zonen (unter Kurs)</div>{z.value.bids.map((x) => row(x, "b"))}</div>
    </div>
  );
}

function Derivs({ d, liq }) {
  const l = liq.value || {};
  return (
    <Card title="Derivate & Liquidationen" testid="ami-intel-derivs" actions={<KindBadge kind="beobachtung" />}>
      <StatusRow label="Funding" value={d.funding.value == null ? "–" : `${num(d.funding.value * 100, 4)} %`} ok />
      <StatusRow label="Open Interest" value={d.open_interest_btc.value == null ? "–" : `${num(d.open_interest_btc.value, 0)} BTC`} ok />
      <StatusRow label="OI-Änderung 1 h / 24 h" value={`${pct(d.oi_change_1h_pct.value)} / ${pct(d.oi_change_24h_pct.value)}`} ok />
      <StatusRow label="Basis Swap–Spot" value={pct(d.basis_pct.value, 3)} ok />
      <StatusRow label="Liquidiert 24 h (Long / Short)" value={`${num(l.long?.btc ?? 0, 1)} / ${num(l.short?.btc ?? 0, 1)} BTC`} ok testid="ami-intel-liq" />
      <div className="mt-3 flex items-center gap-2 text-sm" data-testid="ami-intel-squeeze"><KindBadge kind="hypothese" />{d.squeeze.value || "–"}{d.squeeze.note && <span className="text-xs text-muted-foreground">({d.squeeze.note})</span>}</div>
    </Card>
  );
}

function Hypotheses({ a, s }) {
  return (
    <Card title="Auffälligkeiten (6 h)" testid="ami-intel-hypotheses" actions={<KindBadge kind="hypothese" />}>
      <p className="text-xs text-muted-foreground mb-3">{s.note}</p>
      {a.value.length + s.value.length === 0 ? <NotYet text="Keine Auffälligkeiten erkannt." /> : (
        <div className="space-y-1 text-xs font-mono">
          {a.value.map((x) => <div key={"a" + x.ts}>{tsFmt(x.ts)} · Absorption ({x.absorption.replace("_", " ")}) · Delta {num(x.delta, 1)} BTC</div>)}
          {s.value.map((x) => <div key={x.id}>{tsFmt(x.ts)} · {x.side === "bid" ? "Bid" : "Ask"}-Wand {num(x.max_btc, 0)} BTC @ {num(x.px, 0)} nach {num(x.lifetime_s, 0)} s verschwunden</div>)}
        </div>
      )}
    </Card>
  );
}

function Events() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ami-events"], queryFn: () => amiGet("/events", { days_back: 30, days_fwd: 7 }) });
  const sync = useMutation({ mutationFn: () => amiPost("/events/sync"), onSuccess: (r) => { toast.success(`${r.events} Termine aktualisiert`); qc.invalidateQueries({ queryKey: ["ami-events"] }); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <Card title="Marktreaktionsdatenbank" testid="ami-events" actions={<Button size="sm" variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending} data-testid="ami-events-sync">Jetzt abrufen</Button>}>
      <p className="text-xs text-muted-foreground mb-3">{q.data?.source} · letzter Abruf {isoFmt(q.data?.sync?.last_at)}{q.data?.sync?.error ? ` · Fehler: ${q.data.sync.error}` : ""}. Reaktionen (BTC-Swap) werden 24 h nach dem Termin aus eigenen Kursdaten berechnet.</p>
      {q.isLoading ? <Loading /> : (q.data.events.length === 0 ? <NotYet text="Noch keine Termine gespeichert." /> : (
        <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="ami-events-table">
          <thead><tr className="text-left uppercase tracking-wider text-muted-foreground"><th className="py-2">Zeit</th><th>Land</th><th>Termin</th><th>Wichtig.</th><th>Prognose</th><th>Vorwert</th><th>15m</th><th>1h</th><th>24h</th></tr></thead>
          <tbody className="font-mono">{q.data.events.map((e) => (
            <tr key={e.id} className="border-t border-border/60"><td className="py-1.5">{tsFmt(e.ts)}</td><td>{e.country}</td><td className="font-sans">{e.title}</td><td>{e.impact === "High" ? "hoch" : "mittel"}</td><td>{e.forecast || "–"}</td><td>{e.previous || "–"}</td>
              <td>{pct(e.reaction?.["15m"])}</td><td>{pct(e.reaction?.["1h"])}</td><td>{pct(e.reaction?.["24h"])}</td></tr>
          ))}</tbody>
        </table></div>
      ))}
    </Card>
  );
}

export default function AmiIntel() {
  const [inst, setInst] = useState("BTC-USDT-SWAP");
  const q = useQuery({ queryKey: ["ami-intel", inst], queryFn: () => amiGet("/intel/live", { inst }), refetchInterval: 10000 });
  const d = q.data;
  return (
    <div className="space-y-6" data-testid="ami-intel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border p-1 bg-secondary/40">
          {[["BTC-USDT-SWAP", "Perpetual Swap"], ["BTC-USDT", "Spot"]].map(([k, l]) => (
            <button key={k} onClick={() => setInst(k)} data-testid={`ami-intel-inst-${k}`} className={`px-3 py-1 rounded-full text-xs transition-colors ${inst === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {d && <span className="text-xs text-muted-foreground" data-testid="ami-stream-state">Datenstrom {d.stream.connected ? "verbunden" : "getrennt"} · {d.stream.reconnects} Neuverbindungen</span>}
      </div>
      {q.isError && <NotYet text={errMsg(q.error)} />}
      {!d ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Kurs (Stream)" value={`${num(d.last, 1)} $`} testid="ami-intel-last" />
            {["0.1", "0.25", "0.5"].map((k) => <Kpi key={k} label={`Orderbuch-Imbalance ±${k.replace(".", ",")} %`} value={d.book_imbalance_pct[k].value == null ? "–" : `${num(d.book_imbalance_pct[k].value, 1)} %`} hint={`positiv = mehr Bid-Volumen · Tiefe ±${num(d.book_depth_pct, 2)} %`} testid={`ami-intel-imb-${k}`} />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><Flow f={d.flow} /><Derivs d={d.derivatives} liq={d.liquidations_24h} /></div>
          <Card title="CVD & Delta (24 h)" testid="ami-intel-cvd" actions={<KindBadge kind="beobachtung" />}><CvdChart inst={inst} /></Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Liquiditätszonen im Orderbuch" testid="ami-intel-zones-card" actions={<KindBadge kind="beobachtung" />}><Zones z={d.liquidity_zones} /><p className="mt-2 text-xs text-muted-foreground">{d.liquidity_zones.note}</p></Card>
            <Hypotheses a={d.absorption} s={d.spoof_hypotheses} />
          </div>
        </>
      )}
      <Events />
    </div>
  );
}
